// SettingsPage.tsx
// ✅ Prepared By fields
// ✅ Logo upload (stored in localStorage via App's userSettings persistence)
// (Proposal sections are edited in place on the Proposal page by org admins —
//  the Settings editor for them was removed.)
// ✅ Scope of Work text by Construction Type (optional)
// ✅ Company (printed company details; Company name also updates orgs.name)

import React, { useEffect, useMemo, useState } from "react";
import "./SettingsPage.css";
import { supabase } from "./supabaseClient";
import {
  fetchSowTemplatesRows,
  upsertSowTemplate,
  deleteSowTemplate,
  SowTemplateFullRow,
} from "./sowTemplates";
import { SignatureUploader } from "./components/Settings/SignatureUploader";

// ------------------------------
// TYPES exported for ProposalPage
// ------------------------------
// One definition, owned by proposalSections.ts; re-exported for the modules
// that have always imported it from here (ProposalPage).
export type { ProposalSection, ProposalSectionType } from "./proposalSections";

// Keep this flexible
export type ConstructionTypeId = string;

export type UserSettings = {
  userName?: string | null;
  userPhone?: string | null;
  userEmail?: string | null;

  // Email Proposal templates
  emailSubjectTemplate?: string | null;
  emailBodyTemplate?: string | null;

  companyName?: string | null;
  companyPhone?: string | null;
  companyAddress?: string | null;
  companyWebsite?: string | null;
  license?: string | null;

  logoDataUrl?: string | null;

  // Per-user signature image (public Supabase Storage URL).
  // Printed on EVERY contract: the main contract's Authorized Signature line
  // and the ST-124's "Signature of contractor" (ContractPage).
  signatureUrl?: string | null;



  logoSlogan?: string | null;
proposalLayoutOrder?: string[]; 

};


type SettingsPageProps = {
  userSettings: UserSettings;
  setUserSettings: React.Dispatch<React.SetStateAction<UserSettings>>;

  // ✅ NEW (safe + optional): pass from App.tsx later
  orgId?: string | null;
  isAdmin?: boolean;
  /** Report that the user deliberately emptied a setting (or refilled it),
   *  so App's save lets that blank through the upsertSettings blank-guard. */
  onFieldCleared?: (key: string, cleared: boolean) => void;
  /** orgs.name was updated to match Company name (so App's copy can follow). */
  onOrgNameSynced?: (name: string) => void;
};

// ------------------------------
// Helpers
// ------------------------------


function ensureDefaults(prev: UserSettings): UserSettings {
  const next: UserSettings = { ...(prev || {}) };

  // NOTE: proposalSections and proposalLayoutOrder are deliberately NOT seeded
  // here. They are owned by org_proposal_sections / org_settings, and this
  // function runs on Settings mount BEFORE we know whether the blocks fetch
  // succeeded. Seeding fabricated DEFAULT_SECTIONS ids produced a
  // proposalLayoutOrder full of ids that match no real block, and — because
  // App.tsx caches userSettings on every change (writeCachedSettings runs
  // ungated by settingsHydratedRef) — that fabricated order was written over
  // the cached last-known-good order on an offline launch. The real seeding
  // path is loadProposalSections(), which only offers defaults after an
  // AUTHORITATIVE empty read. (That seeding lived in the Settings section
  // editor, since removed — see the onboarding item in the backlog.)
  if (!next.emailSubjectTemplate) {
    next.emailSubjectTemplate = "Your Proposal – {{clientLastName}}";
  }


  if (!next.emailBodyTemplate) {
    next.emailBodyTemplate =
      "Hi {{clientFirstName}},\n\n" +
      "Thank you for the opportunity to quote your project.\n" +
      "You can view your proposal using the link below.\n\n" +
      "If you have any questions, reply here or call/text me at {{userPhone}}.\n\n" +
      "Thanks,\n" +
      "{{userName}}\n" +
      "{{companyName}}";
  }

  return next;
}

export default function SettingsPage({
  userSettings,
  setUserSettings,
  orgId = null,
  isAdmin = true, // ✅ TEMP default so this won't break until you wire real admin logic from App.tsx
  onFieldCleared,
  onOrgNameSynced,
}: SettingsPageProps) {
const defaultsAppliedRef = React.useRef(false);

  // Company name is the ONE company name (merged 2026-09-23): the printed
  // org_settings.company_name. Editing it also updates orgs.name (debounced)
  // so everything that still reads orgs.name — Users/Licenses, org creation —
  // stays in step. A cleared name is not pushed: orgs.name may not be blank.
  const orgNameSyncTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const syncOrgsName = (name: string) => {
    if (!isAdmin || !orgId) return;
    if (orgNameSyncTimerRef.current) clearTimeout(orgNameSyncTimerRef.current);
    const next = name.trim();
    if (!next) return;
    orgNameSyncTimerRef.current = setTimeout(async () => {
      const { error } = await supabase
        .from("orgs")
        .update({ name: next })
        .eq("id", orgId);
      if (error) console.warn("orgs.name sync failed:", error.message);
      else onOrgNameSynced?.(next);
    }, 800);
  };
  useEffect(
    () => () => {
      if (orgNameSyncTimerRef.current) clearTimeout(orgNameSyncTimerRef.current);
    },
    []
  );

  // =========================================================
  // SOW TEMPLATES (shared in Supabase)
  // =========================================================
  const [sowRows, setSowRows] = useState<SowTemplateFullRow[]>([]);
  const [sowLoading, setSowLoading] = useState(false);
  const [sowError, setSowError] = useState<string | null>(null);

  const [selectedSowKey, setSelectedSowKey] = useState<string>("");
  const selectedSowRow = useMemo(
    () => sowRows.find((r) => r.construction_key === selectedSowKey) || null,
    [sowRows, selectedSowKey]
  );

  const [editLabel, setEditLabel] = useState("");
  const [editBody, setEditBody] = useState("");

  async function loadSowTemplates() {
    setSowLoading(true);
    setSowError(null);
    try {
      const rows = await fetchSowTemplatesRows();
      setSowRows(rows);

      // pick first row if nothing selected
      if (!selectedSowKey && rows.length) {
        setSelectedSowKey(rows[0].construction_key);
      }
      // if selected key no longer exists, fallback to first
      if (selectedSowKey && !rows.some((r) => r.construction_key === selectedSowKey)) {
        setSelectedSowKey(rows[0]?.construction_key || "");
      }
    } catch (e: any) {
      setSowError(String(e?.message || e || "Failed to load SOW templates"));
    } finally {
      setSowLoading(false);
    }
  }

  useEffect(() => {
    loadSowTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // when user picks a different template, load it into editor fields
  useEffect(() => {
    if (!selectedSowRow) {
      setEditLabel("");
      setEditBody("");
      return;
    }
    setEditLabel(selectedSowRow.label || "");
    setEditBody(selectedSowRow.body || "");
  }, [selectedSowRow]);

  function normalizeKey(input: string) {
    return (input || "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "_")
      .replace(/-+/g, "_")
      .replace(/[^a-z0-9_]/g, "");
  }

  async function onAddSowTemplate() {
    if (!isAdmin) return;

    const raw = window.prompt(
      "Enter a new Construction Key (example: new_construction, resurface, second_story)"
    );
    const key = normalizeKey(raw || "");
    if (!key) return;

    const label = window.prompt("Enter a label (example: New Construction)") || key;

    try {
      await upsertSowTemplate({ construction_key: key, label, body: "" });
      await loadSowTemplates();
      setSelectedSowKey(key);
    } catch (e: any) {
      window.alert(String(e?.message || e || "Failed to add template"));
    }
  }

  async function onSaveSowTemplate() {
    if (!isAdmin || !selectedSowKey) return;

    try {
      await upsertSowTemplate({
        construction_key: selectedSowKey,
        label: editLabel || selectedSowKey,
        body: editBody || "",
      });
      await loadSowTemplates();
      window.alert("Saved.");
    } catch (e: any) {
      window.alert(String(e?.message || e || "Failed to save template"));
    }
  }

  async function onDeleteSowTemplate() {
    if (!isAdmin || !selectedSowKey) return;

    const ok = window.confirm(`Delete template "${selectedSowKey}"? This cannot be undone.`);
    if (!ok) return;

    try {
      await deleteSowTemplate(selectedSowKey);
      setSelectedSowKey("");
      await loadSowTemplates();
      window.alert("Deleted.");
    } catch (e: any) {
      window.alert(String(e?.message || e || "Failed to delete template"));
    }
  }

 useEffect(() => {
  if (defaultsAppliedRef.current) return;
  defaultsAppliedRef.current = true;

  setUserSettings((prev) => ensureDefaults(prev || {}));
}, [setUserSettings]);



const settings = userSettings || {};

  const update = (patch: Partial<UserSettings>) => {
    setUserSettings((prev) => ({ ...(prev || {}), ...patch }));
  };

  /** Company fields: admin-only, and an emptied field is reported so the
   *  save stores it as empty rather than restoring the previous value. */
  const updateCompanyField = (
    key: "companyName" | "companyPhone" | "companyWebsite" | "companyAddress",
    value: string
  ) => {
    if (!isAdmin) return;
    update({ [key]: value } as Partial<UserSettings>);
    onFieldCleared?.(key, value.trim() === "");
    if (key === "companyName") syncOrgsName(value);
  };



 
  const onLogoPick = (file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => update({ logoDataUrl: String(reader.result || "") });
    reader.readAsDataURL(file);
  };

  return (
    <div className="settings-shell">
      <div className="settings-top">
        <div className="settings-kicker">SYSTEM</div>
  
        <div className="settings-sub">Proposal branding + section builder + scope templates.</div>
      </div>

      <div className="settings-content">
        {/* Company — the identity that PRINTS on proposals, contracts and
            emails (org_settings.company_*). Org-wide, admin-edited. Empty
            means empty: no fallback values are filled in here. */}
        <SectionCard
          title="Company"
          subtitle="Printed on your proposals, contracts and emails, and shown across the app. Admins can edit."
        >
          <div className="grid-3">
            {(
              [
                ["companyName", "Company name", "text", "e.g. Acme Decks, Inc."],
                ["companyPhone", "Company phone", "tel", "e.g. (555) 555-0100"],
                ["companyWebsite", "Website", "text", "e.g. acmedecks.com"],
              ] as const
            ).map(([key, label, type, placeholder]) => (
              <Field key={key} label={label}>
                <input
                  className="ui-input"
                  type={type}
                  value={(settings as any)[key] || ""}
                  onChange={(e) => updateCompanyField(key, e.target.value)}
                  disabled={!isAdmin}
                  placeholder={placeholder}
                />
              </Field>
            ))}
            <Field label="Company address" spanAll>
              <input
                className="ui-input"
                value={settings.companyAddress || ""}
                onChange={(e) =>
                  updateCompanyField("companyAddress", e.target.value)
                }
                disabled={!isAdmin}
                placeholder="e.g. 100 Main Street"
              />
            </Field>
          </div>
          {!isAdmin ? (
            <div className="micro" style={{ marginTop: 8 }}>
              You have read-only access. Ask an admin to edit company details.
            </div>
          ) : null}
        </SectionCard>

        {/* Prepared By */}
        <SectionCard title="Prepared By" subtitle="These fields appear on your proposal header.">
          <div className="grid-3">
            <Field label="Name">
              <input
                className="ui-input"
                value={settings.userName || ""}
                onChange={(e) => update({ userName: e.target.value })}
                placeholder="Your name"
              />
            </Field>

            <Field label="Phone">
              <input
                className="ui-input"
                value={settings.userPhone || ""}
                onChange={(e) => update({ userPhone: e.target.value })}
                placeholder="(631) 555-1234"
              />
            </Field>

            <Field label="Email">
              <input
                className="ui-input"
                value={settings.userEmail || ""}
                onChange={(e) => update({ userEmail: e.target.value })}
                placeholder="you@company.com"
              />
            </Field>
          </div>
        </SectionCard>

        {/* Signature — signs every contract: the Authorized Signature line
            and the ST-124 (ContractPage). */}
        <SectionCard
          title="Signature"
          subtitle="Signs every contract you print: the Authorized Signature line on the contract, and the contractor signature on the ST-124 Capital Improvement Certificate."
        >
          <SignatureUploader
            currentUrl={settings.signatureUrl}
            onSaved={(url) => update({ signatureUrl: url })}
          />
        </SectionCard>

        {/* Email Proposal Templates */}
        <SectionCard
          title="Email Proposal"
          subtitle="These templates are used when you click File → Email Proposal. Use placeholders like {{clientLastName}}."
        >
          <div className="grid-3">
            <Field label="Subject" spanAll>
              <input
                className="ui-input"
                value={settings.emailSubjectTemplate || ""}
                onChange={(e) => update({ emailSubjectTemplate: e.target.value })}
                placeholder="Your Proposal – {{clientLastName}}"
              />
            </Field>

            <Field label="Email Body" spanAll>
              <textarea
                className="ui-textarea"
                value={settings.emailBodyTemplate || ""}
                onChange={(e) => update({ emailBodyTemplate: e.target.value })}
                rows={9}
                placeholder={
                  "Hi {{clientFirstName}},\n\n" +
                  "Thank you...\n\n" +
                  "Thanks,\n{{userName}}"
                }
              />
            </Field>
          </div>

          <div className="micro" style={{ marginTop: 8 }}>
            Available placeholders: {"{{clientFirstName}}"} {"{{clientLastName}}"}{" "}
            {"{{clientEmail}}"} {"{{userName}}"} {"{{userPhone}}"} {"{{companyName}}"}
          </div>
        </SectionCard>

        {/* Logo */}
        <SectionCard
          title="Logo"
          subtitle="Upload a PNG with transparency for the cleanest look."
          
          right={
            settings.logoDataUrl ? (
              <button
                type="button"
                className="ui-btn danger"
                onClick={() => update({ logoDataUrl: "" })}
              >
                Remove
              </button>
            ) : null
          }
        >
          <div className="logo-row">
            <div className="logo-preview">
              {settings.logoDataUrl ? (
                <img src={settings.logoDataUrl} alt="Logo" className="logo-img" />
              ) : (
                <div className="logo-empty">No logo</div>
              )}
            </div>

<div className="micro" style={{ marginTop: 8 }}>
  Tip: keep this short (1 line looks best).
</div>

            <div className="logo-actions">
              <label className="file-btn">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => onLogoPick(e.target.files?.[0] || null)}
                />
                Upload Logo
              </label>
              <div className="micro">Tip: keep it wide, not tall. Transparent background looks best.</div>
            </div>
            <div style={{ marginTop: 14 }}>
  <Field label="Logo Slogan (prints under logo)" spanAll>
    <input
      className="ui-input"
      type="text"
      placeholder='Example: "Premium Outdoor Construction"'
      value={settings.logoSlogan || ""}
      onChange={(e) => update({ logoSlogan: e.target.value })}
    />
  </Field>
  <div className="micro" style={{ marginTop: 6 }}>
    Tip: keep it short (1 line).
  </div>
</div>

          </div>
        </SectionCard>



        {/* Scope of Work */}
        <section className="settings-card">
          <div className="settings-card-title">SCOPE TEMPLATES</div>
          <div className="settings-card-subtitle">
            Shared Scope of Work templates stored in Supabase. Admin can edit. Users are read-only.
          </div>

          <div style={{ display: "flex", gap: 14, marginTop: 12, alignItems: "flex-start" }}>
            {/* LEFT: Add + Dropdown */}
            <div style={{ width: 320, flex: "0 0 320px" }}>
              {isAdmin ? (
                <button type="button" className="btn btn-secondary" onClick={onAddSowTemplate}>
                  + Add Construction Type
                </button>
              ) : null}

              <div style={{ marginTop: 12 }}>
                {sowLoading ? (
                  <div style={{ opacity: 0.7 }}>Loading templates…</div>
                ) : sowError ? (
                  <div style={{ opacity: 0.7 }}>{sowError}</div>
                ) : sowRows.length === 0 ? (
                  <div style={{ opacity: 0.7 }}>No scope templates yet.</div>
                ) : (
                  <select
                    value={selectedSowKey}
                    onChange={(e) => setSelectedSowKey(e.target.value)}
                    style={{ width: "100%", padding: 10, borderRadius: 10 }}
                  >
                    {sowRows.map((r) => (
                      <option key={r.construction_key} value={r.construction_key}>
                        {r.label} ({r.construction_key})
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            {/* RIGHT: Editor */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {!selectedSowRow ? (
                <div style={{ opacity: 0.7, marginTop: 6 }}>Pick a type from the dropdown to edit.</div>
              ) : (
                <>
                  <div style={{ display: "grid", gap: 8, marginBottom: 10 }}>
                    <label style={{ fontWeight: 700 }}>Label</label>
                    <input
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      disabled={!isAdmin}
                      style={{ width: "100%", padding: 10, borderRadius: 10 }}
                      placeholder="Ex: New Construction"
                    />
                  </div>

                  <div style={{ display: "grid", gap: 8 }}>
                    <label style={{ fontWeight: 700 }}>
                      Template Text (AUTO scope shown on proposals)
                    </label>
                    <textarea
                      value={editBody}
                      onChange={(e) => setEditBody(e.target.value)}
                      disabled={!isAdmin}
                      rows={10}
                      style={{ width: "100%", padding: 10, borderRadius: 10 }}
                      placeholder="Type the Scope of Work template here…"
                    />
                  </div>

                  <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                    {isAdmin ? (
                      <>
                        <button type="button" className="btn btn-primary" onClick={onSaveSowTemplate}>
                          Save
                        </button>
                        <button type="button" className="btn btn-outline" onClick={onDeleteSowTemplate}>
                          Delete
                        </button>
                      </>
                    ) : (
                      <div style={{ opacity: 0.7 }}>
                        You have read-only access. Ask an admin to update template language.
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </section>

        <div className="settings-footer-spacer" />
      </div>
    </div>
  );
}
// ------------------------------
// Small UI helpers (same file)
// ------------------------------
function SectionCard({
  title,
  subtitle,
  right,
  children,
}: {
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="card">
      <div className="card-head">
        <div>
          <div className="card-title">{title}</div>
          {subtitle ? <div className="card-sub">{subtitle}</div> : null}
        </div>
        {right ? <div className="card-right">{right}</div> : null}
      </div>
      <div className="card-body">{children}</div>
    </div>
  );
}

function Field({
  label,
  spanAll,
  children,
}: {
  label: string;
  spanAll?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className={`field ${spanAll ? "span-all" : ""}`}>
      <div className="field-label">{label}</div>
      {children}
    </label>
  );
}
